import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { showToast } from 'hfs-common-ui';
import {
  checkFotaActionTypes,
  controlButtonsActionsTypes,
  rebootActionTypes,
} from '../../utils/actionsTypes';
import { useViewType } from '~/hooks/use-view-type';
import { useAssetLookup } from '~/domain/assets/hooks/use-asset-lookup';
import type { AppState } from '~/reducers';
import { clearOperation } from '~/domain/assets/store/hfs-operations/reducer';
import { checkIfOperationIsFailed } from '~/domain/assets/utils/hfs-operations-utils';
import MobileOperationResultIndicatorModal from './MobileOperationResultIndicatorModal';
import { formatOperationFailureIndication } from './utils';
import type { OperationFailureIndicatorItem } from './types';

export const OperationFailureIndicator = () => {
  const { mobileView } = useViewType();
  const dispatch = useDispatch();
  const [failureDetails, setFailureDetails] = useState<OperationFailureIndicatorItem | undefined>();
  const { findAsset } = useAssetLookup();
  const actionTypes = new Set([
    ...controlButtonsActionsTypes,
    ...rebootActionTypes,
    ...checkFotaActionTypes,
  ]);
  const failedOperations = useSelector((state: AppState) =>
    state.assetHfsOperations.operations.filter(
      (operation) =>
        checkIfOperationIsFailed(operation) &&
        actionTypes.has(operation.hfsActionId) &&
        operation.executionType !== 'BATCH'
    )
  );

  useEffect(() => {
    if (mobileView) {
      if (failedOperations[0] !== undefined && failureDetails === undefined) {
        setFailureDetails(
          formatOperationFailureIndication(
            findAsset(failedOperations[0].iprId),
            failedOperations[0]
          )
        );
      }
    } else {
      for (const commandError of failedOperations) {
        const asset = findAsset(commandError.iprId);
        const error = formatOperationFailureIndication(asset, commandError);
        showToast({
          message: error.toastMessage,
          title: error.toastTitle,
          icon: 'notification.error',
          iconColor: 'red500',
          autoClose: 10000,
          position: 'top-right',
        });
        dispatch(
          clearOperation({
            iprId: commandError.iprId,
            hfsActionId: commandError.hfsActionId,
          })
        );
      }
    }
  }, [failedOperations, findAsset, mobileView, dispatch, failureDetails]);

  const dismissCallback = useCallback(() => {
    dispatch(
      clearOperation({
        iprId: failureDetails.iprId,
        hfsActionId: failureDetails.hfsActionId,
      })
    );
    setFailureDetails(undefined);
  }, [dispatch, failureDetails]);

  if (failureDetails === undefined) {
    return null;
  }

  return (
    <MobileOperationResultIndicatorModal
      title={failureDetails.modalTitle}
      iconType="notification.error"
      iconColor="red500"
      message={failureDetails.modalMessage}
      dismissCallback={dismissCallback}
    />
  );
};

export default OperationFailureIndicator;
